import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Send, Loader2, Users, Hash, Globe, SmilePlus, Trash2, AtSign, Pin, CheckCircle2, Paperclip, FileText, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');
const GLOBAL_ROOM_ID = 'workspace';
const QUICK_EMOJIS = ['👍', '❤️', '🎉', '✅', '👀', '🔥', '🚀', '🙏'];
const TYPING_IDLE_MS = 3000;

/**
 * Live chat panel used on /workspace. Supports BOTH a single global thread
 * (room_id='workspace') and any workspace Room (room_id=<uuid>). Real-time
 * fan-out via WebSocket, @mention autocomplete, typing indicator, and emoji
 * reactions — all backed by the per-room ChatWSManager on the server.
 */
export default function WorkspaceChat({ rooms = [], hideRoomSelector = false, initialRoomId = GLOBAL_ROOM_ID, variant = 'default' }) {
  const { token, user } = useAuth();
  const [activeRoomId, setActiveRoomId] = useState(initialRoomId);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState([]);
  const [typingUsers, setTypingUsers] = useState({}); // user_id → {user_name, last_ts}
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionCandidates, setMentionCandidates] = useState([]);
  const [reactionPickerFor, setReactionPickerFor] = useState(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachQuery, setAttachQuery] = useState('');
  const [attachCandidates, setAttachCandidates] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]); // [{note_id, title, shared}]

  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const inputRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const activeRoomLabel =
    activeRoomId === GLOBAL_ROOM_ID
      ? 'Global Workspace'
      : (rooms.find(r => r.room_id === activeRoomId)?.name || 'Room');

  // ─── Load history when room changes ─────────────────────────────────
  const loadMessages = useCallback(async (rid) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/chat/messages`, { headers: authHeaders, params: { room_id: rid, limit: 100 } });
      setMessages(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load chat history');
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { loadMessages(activeRoomId); setTypingUsers({}); }, [activeRoomId, loadMessages]);

  // ─── WebSocket connection ──────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    const url = `${WS_BASE}/ws/chat/${activeRoomId}/${encodeURIComponent(token)}`;
    let ws;
    try { ws = new WebSocket(url); }
    catch (e) { console.error('WS open failed:', e); return; }
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      if (ev.data === 'pong') return;
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.event === 'message') {
        setMessages(prev => {
          if (prev.some(m => m.message_id === msg.message.message_id)) return prev;
          return [...prev, msg.message];
        });
        // Remove sender from typing users
        setTypingUsers(prev => {
          const next = { ...prev };
          delete next[msg.message.user_id];
          return next;
        });
      } else if (msg.event === 'reaction') {
        setMessages(prev => prev.map(m => m.message_id === msg.message_id ? { ...m, reactions: msg.reactions } : m));
      } else if (msg.event === 'delete') {
        setMessages(prev => prev.filter(m => m.message_id !== msg.message_id));
      } else if (msg.event === 'pinned') {
        setMessages(prev => prev.map(m => m.message_id === msg.message_id
          ? { ...m, pinned_note_id: msg.note_id, pinned_by_name: msg.pinned_by_name, pinned_at: msg.pinned_at }
          : m));
      } else if (msg.event === 'presence') {
        setOnline(msg.online || []);
      } else if (msg.event === 'typing') {
        if (msg.user_id === user?.user_id) return;
        setTypingUsers(prev => ({ ...prev, [msg.user_id]: { user_name: msg.user_name, ts: Date.now() } }));
      }
    };
    ws.onerror = () => { /* silent — reconnect on room change */ };

    const ping = setInterval(() => { try { ws.readyState === 1 && ws.send('ping'); } catch {} }, 30000);
    // Clean typing users after idle
    const cleanTyping = setInterval(() => {
      setTypingUsers(prev => {
        const cutoff = Date.now() - TYPING_IDLE_MS;
        const next = {};
        for (const [uid, v] of Object.entries(prev)) if (v.ts > cutoff) next[uid] = v;
        return next;
      });
    }, 1000);

    return () => {
      clearInterval(ping);
      clearInterval(cleanTyping);
      try { ws.close(); } catch {}
    };
  }, [token, activeRoomId, user?.user_id]);

  // ─── Auto-scroll to bottom when new messages arrive ────────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typingUsers]);

  // ─── Mention autocomplete ──────────────────────────────────────────
  const handleDraftChange = async (e) => {
    const val = e.target.value;
    setDraft(val);

    // Emit typing (throttled)
    if (wsRef.current && wsRef.current.readyState === 1) {
      const now = Date.now();
      if (now - lastTypingSentRef.current > 1500) {
        try { wsRef.current.send(JSON.stringify({ event: 'typing' })); } catch {}
        lastTypingSentRef.current = now;
      }
    }
    // Clear existing typing timer
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {}, TYPING_IDLE_MS);

    // Detect @mention trigger at caret
    const caret = e.target.selectionStart;
    const upToCaret = val.substring(0, caret);
    const mMention = upToCaret.match(/@([\w.-]*)$/);
    // Detect /attach <query> slash command (only when it's at the start of a line or right after whitespace)
    const mAttach = upToCaret.match(/(?:^|\s)\/attach\s+(.{0,60})$/);

    if (mAttach) {
      setAttachOpen(true); setMentionOpen(false);
      const q = mAttach[1].trim();
      setAttachQuery(q);
      try {
        const res = await axios.get(`${API}/chat/search-notes`, { headers: authHeaders, params: { q } });
        setAttachCandidates(res.data);
      } catch { setAttachCandidates([]); }
    } else if (mMention) {
      setMentionOpen(true); setAttachOpen(false);
      setMentionQuery(mMention[1]);
      try {
        const res = await axios.get(`${API}/chat/mention-candidates`, { headers: authHeaders, params: { q: mMention[1] } });
        setMentionCandidates(res.data);
      } catch { setMentionCandidates([]); }
    } else {
      setMentionOpen(false);
      setAttachOpen(false);
    }
  };

  const insertMention = (candidate) => {
    if (!candidate?.handle) return;
    // Replace the trailing @partial with the full handle
    const caret = inputRef.current?.selectionStart ?? draft.length;
    const before = draft.substring(0, caret).replace(/@([\w.-]*)$/, `@${candidate.handle}`);
    const after = draft.substring(caret);
    const next = before + ' ' + after;
    setDraft(next);
    setMentionOpen(false);
    setTimeout(() => { inputRef.current?.focus(); }, 0);
  };

  const attachNote = (note) => {
    if (pendingAttachments.some(a => a.note_id === note.note_id)) {
      toast.info('Note already attached');
      setAttachOpen(false);
      return;
    }
    if (pendingAttachments.length >= 5) {
      toast.error('Max 5 attachments per message');
      setAttachOpen(false);
      return;
    }
    // Strip the "/attach <query>" token from draft — the attachment chip replaces it
    const caret = inputRef.current?.selectionStart ?? draft.length;
    const before = draft.substring(0, caret).replace(/(?:^|\s)\/attach\s+.{0,60}$/, (match) => match.startsWith(' ') ? ' ' : '');
    const after = draft.substring(caret);
    setDraft((before + after).trim() === '' ? '' : before + after);
    setPendingAttachments(prev => [...prev, { note_id: note.note_id, title: note.title, shared: note.shared }]);
    setAttachOpen(false);
    setTimeout(() => { inputRef.current?.focus(); }, 0);
  };

  const removeAttachment = (note_id) => {
    setPendingAttachments(prev => prev.filter(a => a.note_id !== note_id));
  };

  // ─── Send ──────────────────────────────────────────────────────────
  const sendMessage = async () => {
    const content = draft.trim();
    if (!content && pendingAttachments.length === 0) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/messages`, {
        room_id: activeRoomId,
        content,
        attached_note_ids: pendingAttachments.map(a => a.note_id),
      }, { headers: authHeaders });
      if (res.data?.cop_signal_id) {
        toast.success(
          `Friction captured (${res.data.cop_signal_type}) — opened as a CoP signal`,
          {
            action: { label: 'View', onClick: () => window.open('/cop', '_blank') },
          },
        );
      }
      setDraft('');
      setPendingAttachments([]);
      setMentionOpen(false);
      setAttachOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Reactions ─────────────────────────────────────────────────────
  const toggleReaction = async (message_id, emoji) => {
    try {
      await axios.post(`${API}/chat/messages/${message_id}/react`, { emoji }, { headers: authHeaders });
      setReactionPickerFor(null);
    } catch (e) { toast.error('Reaction failed'); }
  };

  const deleteMessage = async (message_id) => {
    if (!window.confirm('Delete this message?')) return;
    try { await axios.delete(`${API}/chat/messages/${message_id}`, { headers: authHeaders }); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const pinToPkm = async (msg) => {
    if (msg.pinned_note_id) {
      window.open(`/pkm?note_id=${msg.pinned_note_id}`, '_blank');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/chat/messages/${msg.message_id}/pin-to-pkm`,
        { include_context: true },
        { headers: authHeaders },
      );
      if (res.data.already_pinned) {
        toast.info('Already in Knowledge Engine — opening…');
      } else {
        toast.success(`Pinned to Knowledge Engine as "${res.data.note_title}"`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pin failed');
    }
  };

  // ─── Helpers ───────────────────────────────────────────────────────
  const renderContentWithMentions = (content, mentions) => {
    if (!mentions?.length) return content;
    const handles = new Set(mentions.map(m => m.user_name.toLowerCase()));
    return content.split(/(@[\w.-]+)/g).map((part, i) => {
      if (part.startsWith('@')) {
        const handleText = part.substring(1).toLowerCase();
        const isMatch = [...handles].some(h => h.includes(handleText) || handleText.includes(h.split(' ')[0].toLowerCase()));
        if (isMatch) {
          return <span key={i} className="bg-[#002FA7]/10 text-[#002FA7] font-semibold rounded px-1">{part}</span>;
        }
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  };

  const typingList = Object.values(typingUsers).map(t => t.user_name).filter(Boolean);

  return (
    <div
      className={variant === 'embedded'
        ? 'flex gap-0 h-full'
        : 'flex gap-4 h-[calc(100vh-260px)] min-h-[500px]'}
      data-testid="workspace-chat"
    >
      {/* Sidebar: room selector */}
      {!hideRoomSelector && (
      <Card className="w-64 border-[#E5E7EB] flex flex-col overflow-hidden">
        <div className="px-3 py-2 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Channels</p>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          <button
            type="button"
            onClick={() => setActiveRoomId(GLOBAL_ROOM_ID)}
            className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
              activeRoomId === GLOBAL_ROOM_ID ? 'bg-[#002FA7] text-white' : 'hover:bg-[#F3F4F6] text-[#0A0A0A]'
            }`}
            data-testid="chat-channel-workspace"
          >
            <Globe className="w-3.5 h-3.5" /> Global Workspace
          </button>
          {rooms.map(r => (
            <button
              key={r.room_id}
              type="button"
              onClick={() => setActiveRoomId(r.room_id)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                activeRoomId === r.room_id ? 'bg-[#002FA7] text-white' : 'hover:bg-[#F3F4F6] text-[#0A0A0A]'
              }`}
              data-testid={`chat-channel-${r.room_id}`}
            >
              <Hash className="w-3.5 h-3.5" /> {r.name}
            </button>
          ))}
          {rooms.length === 0 && (
            <p className="text-[11px] text-[#9CA3AF] px-3 py-2">No rooms yet — create one in Live Workspace.</p>
          )}
        </div>
      </Card>
      )}

      {/* Main thread */}
      <Card className="flex-1 border-[#E5E7EB] flex flex-col overflow-hidden">
        <div className="px-4 py-2 border-b border-[#E5E7EB] bg-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            {activeRoomId === GLOBAL_ROOM_ID ? <Globe className="w-4 h-4 text-[#002FA7]" /> : <Hash className="w-4 h-4 text-[#002FA7]" />}
            <h3 className="font-semibold text-[#0A0A0A] text-sm">{activeRoomLabel}</h3>
          </div>
          <Badge variant="outline" className="text-[10px] flex items-center gap-1" data-testid="chat-presence-badge">
            <Users className="w-3 h-3" /> {online.length} online
          </Badge>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFAFA]" data-testid="chat-messages-list">
          {loading ? (
            <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 animate-spin text-[#9CA3AF]" /></div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-[#9CA3AF] text-sm">
              <Send className="w-8 h-8 mb-2 opacity-40" />
              No messages yet — kick off the conversation!
            </div>
          ) : messages.map(m => (
            <div key={m.message_id} className="flex gap-2 group" data-testid={`chat-message-${m.message_id}`}>
              <div className="w-8 h-8 rounded-full bg-[#002FA7] text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                {(m.user_name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-[12px] text-[#0A0A0A]">{m.user_name}</span>
                  <span className="text-[10px] text-[#9CA3AF]">{format(new Date(m.created_at), 'HH:mm · MMM d')}</span>
                  {m.pinned_note_id && (
                    <button
                      type="button"
                      onClick={() => pinToPkm(m)}
                      className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider bg-[#34C759]/10 text-[#0F7A34] px-1.5 py-0.5 rounded hover:bg-[#34C759]/20 transition-colors"
                      title={`Pinned to Knowledge Engine by ${m.pinned_by_name || 'someone'} — click to open`}
                      data-testid={`pinned-badge-${m.message_id}`}
                    >
                      <CheckCircle2 className="w-2.5 h-2.5" /> Pinned
                    </button>
                  )}
                </div>
                <div className="text-[13px] text-[#0A0A0A] whitespace-pre-wrap break-words leading-snug">
                  {renderContentWithMentions(m.content, m.mentions)}
                </div>
                {/* Attached note preview cards */}
                {m.attached_notes && m.attached_notes.length > 0 && (
                  <div className="mt-2 space-y-1.5" data-testid={`attached-notes-${m.message_id}`}>
                    {m.attached_notes.map(a => (
                      <a
                        key={a.note_id}
                        href={`/pkm?note_id=${a.note_id}`}
                        target="_blank" rel="noreferrer"
                        className="block bg-white border-l-4 border-[#002FA7] border border-[#E5E7EB] rounded-r-md rounded-b-md p-2.5 hover:border-[#002FA7] hover:shadow-sm transition-all max-w-xl"
                        data-testid={`attached-note-${a.note_id}`}
                      >
                        <div className="flex items-start gap-2">
                          <FileText className="w-3.5 h-3.5 text-[#002FA7] flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-semibold text-[#0A0A0A] line-clamp-1">{a.title}</p>
                            {a.snippet && <p className="text-[11px] text-[#4B5563] line-clamp-2 mt-0.5">{a.snippet}</p>}
                            {a.tags && a.tags.length > 0 && (
                              <div className="flex gap-1 mt-1 flex-wrap">
                                {a.tags.slice(0, 4).map(t => (
                                  <span key={t} className="text-[9px] text-[#6B7280] bg-[#F3F4F6] rounded px-1.5 py-0.5">#{t}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
                {/* Reactions */}
                {m.reactions && Object.keys(m.reactions).length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {Object.entries(m.reactions).map(([emoji, users]) => {
                      const isMine = users.includes(user?.user_id);
                      return (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => toggleReaction(m.message_id, emoji)}
                          className={`px-2 py-0.5 rounded-full border text-[11px] flex items-center gap-1 transition-colors ${
                            isMine ? 'bg-[#002FA7]/10 border-[#002FA7]/40 text-[#002FA7]' : 'bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7]/40'
                          }`}
                          data-testid={`reaction-${m.message_id}-${emoji}`}
                          title={`${users.length} ${users.length === 1 ? 'person' : 'people'} reacted`}
                        >
                          <span>{emoji}</span><span>{users.length}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {/* Hover actions */}
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 mt-1 relative">
                  <Button
                    variant="ghost" size="sm" className="h-6 text-[10px] px-2"
                    onClick={() => setReactionPickerFor(reactionPickerFor === m.message_id ? null : m.message_id)}
                    data-testid={`react-btn-${m.message_id}`}
                  >
                    <SmilePlus className="w-3 h-3 mr-1" /> React
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    className={`h-6 text-[10px] px-2 ${m.pinned_note_id ? 'text-[#0F7A34]' : 'text-[#4B5563]'}`}
                    onClick={() => pinToPkm(m)}
                    data-testid={`pin-btn-${m.message_id}`}
                    title={m.pinned_note_id ? 'Open pinned note' : 'Pin this thread to the Knowledge Engine'}
                  >
                    <Pin className="w-3 h-3 mr-1" /> {m.pinned_note_id ? 'Open note' : 'Pin to KM'}
                  </Button>
                  {m.user_id === user?.user_id && (
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-[#FF3B30]" onClick={() => deleteMessage(m.message_id)} data-testid={`del-btn-${m.message_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                  {reactionPickerFor === m.message_id && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg p-2 flex gap-1 z-10" data-testid={`emoji-picker-${m.message_id}`}>
                      {QUICK_EMOJIS.map(e => (
                        <button key={e} onClick={() => toggleReaction(m.message_id, e)} className="w-7 h-7 hover:bg-[#F3F4F6] rounded text-base">{e}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {typingList.length > 0 && (
            <div className="text-[11px] text-[#9CA3AF] italic px-10" data-testid="chat-typing-indicator">
              {typingList.slice(0, 3).join(', ')} {typingList.length === 1 ? 'is' : 'are'} typing…
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t border-[#E5E7EB] bg-white p-3 relative">
          {attachOpen && attachCandidates.length > 0 && (
            <div className="absolute bottom-full left-3 mb-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-72 overflow-y-auto min-w-[360px] z-10" data-testid="attach-autocomplete">
              <p className="text-[9px] uppercase tracking-wider text-[#6B7280] font-semibold px-3 py-1.5 border-b border-[#F3F4F6] bg-[#F9FAFB]">Knowledge Engine · /attach</p>
              {attachCandidates.map(n => (
                <button
                  key={n.note_id}
                  type="button"
                  onClick={() => attachNote(n)}
                  className="w-full text-left px-3 py-2 hover:bg-[#F3F4F6] border-b border-[#F3F4F6] last:border-b-0"
                  data-testid={`attach-candidate-${n.note_id}`}
                >
                  <div className="flex items-start gap-2">
                    <FileText className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${n.shared ? 'text-[#34C759]' : 'text-[#9CA3AF]'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-[12px] font-semibold text-[#0A0A0A] line-clamp-1">{n.title}</p>
                        {n.shared && <span className="text-[8px] font-bold uppercase tracking-wider text-[#0F7A34] bg-[#34C759]/10 px-1 py-0.5 rounded">Shared</span>}
                      </div>
                      {n.tags && n.tags.length > 0 && (
                        <div className="flex gap-1 mt-0.5 flex-wrap">
                          {n.tags.slice(0, 4).map(t => <span key={t} className="text-[9px] text-[#6B7280]">#{t}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {mentionOpen && mentionCandidates.length > 0 && (
            <div className="absolute bottom-full left-3 mb-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-56 overflow-y-auto min-w-[240px] z-10" data-testid="mention-autocomplete">
              {mentionCandidates.map(c => (
                <button
                  key={c.user_id}
                  type="button"
                  onClick={() => insertMention(c)}
                  className="w-full text-left px-3 py-1.5 hover:bg-[#F3F4F6] flex items-center gap-2 text-[12px]"
                  data-testid={`mention-candidate-${c.handle}`}
                >
                  <AtSign className="w-3 h-3 text-[#9CA3AF]" />
                  <span className="font-semibold text-[#0A0A0A]">{c.handle}</span>
                  <span className="text-[#9CA3AF]">· {c.user_name}</span>
                </button>
              ))}
            </div>
          )}
          {/* Pending attachments chips */}
          {pendingAttachments.length > 0 && (
            <div className="flex gap-1.5 mb-2 flex-wrap" data-testid="pending-attachments">
              {pendingAttachments.map(a => (
                <div
                  key={a.note_id}
                  className="inline-flex items-center gap-1.5 bg-[#EFF6FF] border border-[#002FA7]/30 rounded-md px-2 py-1 text-[11px]"
                  data-testid={`pending-attachment-${a.note_id}`}
                >
                  <Paperclip className="w-3 h-3 text-[#002FA7]" />
                  <span className="text-[#0A0A0A] max-w-[240px] truncate">{a.title}</span>
                  <button type="button" onClick={() => removeAttachment(a.note_id)} className="text-[#6B7280] hover:text-[#FF3B30]" aria-label="Remove attachment" data-testid={`remove-attachment-${a.note_id}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={draft}
              onChange={handleDraftChange}
              onKeyDown={handleKeyDown}
              placeholder={`Message ${activeRoomLabel}…  (@ to mention · /attach <title> to embed a note · /friction <text> to capture a CoP signal · Enter to send)`}
              className="flex-1 resize-none rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 min-h-[40px] max-h-[120px]"
              rows={1}
              data-testid="chat-draft-input"
              disabled={sending}
            />
            <Button
              onClick={sendMessage}
              disabled={sending || (!draft.trim() && pendingAttachments.length === 0)}
              className="bg-[#002FA7] hover:bg-[#00227A] h-10"
              data-testid="chat-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
